'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { ArrowRight, Bot, Brain, DollarSign, Users } from 'lucide-react';
import { Session } from 'next-auth';

interface HeroSectionProps {
	session: Session | null;
}

export function HeroSection({ session }: HeroSectionProps) {
	const [email, setEmail] = useState('');

	const features = [
		{
			icon: <Users className="h-6 w-6 text-purple-600" />,
			title: 'Smart Prospecting',
			description:
				'AI automatically discovers and qualifies potential customers using advanced search algorithms',
			highlight: 'Real-time lead scoring'
		},
		{
			icon: <Brain className="h-6 w-6 text-blue-600" />,
			title: 'Conversational Training',
			description:
				'Train agents through natural conversations - no JSON or technical knowledge required',
			highlight: 'Chat-based optimization'
		},
		{
			icon: <DollarSign className="h-6 w-6 text-green-600" />,
			title: 'Autonomous Closing',
			description: 'Ask for payment and handle objections autonomously with 94% accuracy',
			highlight: '24/7 sales execution'
		}
	];

	return (
		<section className="relative py-20 px-4 sm:px-6 lg:px-8">
			<div className="max-w-7xl mx-auto">
				<div className="text-center">
					<Badge className="mb-4 bg-orange-100 text-orange-800 hover:bg-orange-100">
						<Bot className="h-4 w-4 mr-1" />
						Train AI Agents to Find Their Own Customers and Get Paid
					</Badge>
					<h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
						<span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent block leading-tight">
							Train Your AI Agents to Find
						</span>
						<span className="text-gray-900 block leading-tight">
							it's Own Customers and Get Paid
						</span>
					</h1>
					<p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
						Your AI agents reach out to prospects, show what they can do, and collect
						payment for their services—all
						<strong className="text-orange-600"> directly on email and linkedin</strong>.
					</p>

					{/* CTA Section */}
					<div className="max-w-2xl mx-auto">
						{session?.user?.email ? (
							<div className="space-y-4">
								<Link href="/agents">
									<Button
										size="lg"
										className="text-lg px-8 py-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
									>
										Go to My Agents
										<ArrowRight className="ml-2 h-5 w-5" />
									</Button>
								</Link>
								<p className="text-sm text-gray-500">
									Welcome back! Ready to train your AI sales team?
								</p>
							</div>
						) : (
							<div className="space-y-6">
								<div className="relative max-w-2xl mx-auto">
									<div className="relative group">
										<div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
										<div className="relative bg-white/90 backdrop-blur-xl rounded-2xl border border-orange-200/50 shadow-2xl">
											<div className="flex items-center p-2">
												<div className="flex-1 relative">
													<div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
														<div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-red-600 rounded-full animate-pulse"></div>
													</div>
													<Input
														type="url"
														placeholder="https://your-mcp-server.com/mcp"
														value={email}
														onChange={(e) => setEmail(e.target.value)}
														className="text-lg pl-8 pr-4 py-6 bg-transparent border-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 font-mono text-sm"
													/>
												</div>
												<a
													href="https://cal.com/up-punyagupta/chat-with-founders"
													target="_blank"
													rel="noopener noreferrer"
													className="ml-2 inline-flex items-center px-8 py-6 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-white font-semibold"
												>
													Connect & Train
													<ArrowRight className="ml-2 h-5 w-5" />
												</a>
											</div>
										</div>
									</div>
								</div>
								<p className="text-sm text-gray-500 text-center">
									Connect your MCP tools and start training sales skills
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}
